import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthLayout } from './AuthLayout.jsx';
import { Button, Field, Input, PasswordInput, ErrorNotice } from '../../components/ui/index.js';
import { useAuth } from '../../lib/auth.jsx';
import { useMutation } from '../../hooks/useApi.js';

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });

  const { mutate, loading, error } = useMutation(async () => {
    const session = await signIn(form);
    // A platform admin owns no merchant, so /dashboard would 404 for them.
    const home = session?.user?.role === 'platform_admin' ? '/admin' : '/dashboard';
    navigate(location.state?.from ?? home, { replace: true });
  });

  const fieldErrors = error?.fieldErrors ?? {};
  // MA-4 surfaces a lockout distinctly, and it deserves more room than a field error.
  const isLockout = error?.code === 'account_locked';

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back. Your payments, providers and webhooks are where you left them."
      footer={<>Don&rsquo;t have an account? <Link to="/register" className="font-medium text-accent-ink hover:underline">Create one</Link></>}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => { e.preventDefault(); mutate().catch(() => {}); }}
        noValidate
      >
        {error && !Object.keys(fieldErrors).length && (
          <ErrorNotice error={error} title={isLockout ? 'Too many attempts' : 'Could not sign in'} />
        )}

        <Field label="Email" error={fieldErrors.email} required>
          {(props) => (
            <Input
              {...props}
              type="email"
              name="email"
              // FE-15: correct autoComplete values.
              autoComplete="email"
              autoFocus
              value={form.email}
              invalid={Boolean(fieldErrors.email)}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="you@company.com"
            />
          )}
        </Field>

        <Field label="Password" error={fieldErrors.password} required>
          {(props) => (
            <PasswordInput
              {...props}
              name="password"
              autoComplete="current-password"
              value={form.password}
              invalid={Boolean(fieldErrors.password)}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            />
          )}
        </Field>

        {/* FE-11: disabled with a spinner while in flight. */}
        <Button type="submit" size="lg" loading={loading} className="w-full">Sign in</Button>
      </form>
    </AuthLayout>
  );
}

export default LoginPage;
